import { getInstanceId } from '@va/dashboard/selectors/app';
import { isWhiteLabelEnabled, selectWebsiteCustomSnippetDomain } from '@va/dashboard/selectors/core';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { CustomDomainPlatformType, CustomDomainType } from '@va/shared/api/custom-domains';
import { CustomWebsiteDomainForm } from '@va/shared/custom-domains';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { getTrackingCode } from '@va/standalone/shared/actions';
import { contributorRole, isAgencyOwner, selectAgencyUi } from '@va/standalone/shared/selectors';
import { fontWeights, Paragraph } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { isKnownSubdomain } from '@va/util/helpers';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTrackingCodeDomainCreationModal } from './creation/CustomTrackingCodeDomainCreationModal';
import { useWebsiteTrackingCodeDomainsActions } from './useWebsiteTrackingCodeDomainsActions';
export const CustomTrackingCodeDomainForm = () => {
  const websiteId = useSelector(getInstanceId);
  const { showErrorNotification } = useAddNotification();

  const { confirm } = useConfirmationDialogContext();

  const actions = useWebsiteTrackingCodeDomainsActions();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const websiteCustomSnippetDomain = useSelector(selectWebsiteCustomSnippetDomain);
  const agencyUI = useSelector(selectAgencyUi);
  const isWLEnabled = useSelector(isWhiteLabelEnabled);
  const isContributor = useSelector(contributorRole);
  const agencyOwner = useSelector(isAgencyOwner);

  const agencySnippetDomain = useMemo(() => {
    if (!isWLEnabled) return undefined;

    const agencyUsingCustomSubdomain = agencyUI?.customUrl ? isKnownSubdomain(agencyUI.customUrl) : false;
    // only the fully custom domain can be used on the snippet at the moment, not the subdomain
    if (agencyUsingCustomSubdomain) return undefined;

    return agencyUI?.customUrl;
  }, [agencyUI?.customUrl, isWLEnabled]);

  const isCustomDomainEditEnabled = useMemo(() => {
    if (isContributor) return false;
    if (!isWLEnabled) return true;

    // if the website had a custom domain before it was white-labeled, we need to allow edit
    if (websiteCustomSnippetDomain) return true;

    // the agency domain is not available to the snippet - in the case of subdomains
    if (!agencySnippetDomain) return true;

    // will use the agency domain
    return false;
  }, [agencySnippetDomain, isContributor, isWLEnabled, websiteCustomSnippetDomain]);

  const inputTooltip = useMemo(() => {
    if (isCustomDomainEditEnabled) return undefined;

    if (agencyOwner) {
      return translate('standalone.customDomains.trackingCode.agencyAdminEditNotAllowed');
    }

    return translate('standalone.customDomains.trackingCode.editNotAllowed');
  }, [agencyOwner, isCustomDomainEditEnabled, translate]);

  return (
    <>
      <TooltipWrapper
        content={translate('standalone.customDomains.trackingCode.formTooltip')}
        trigger='click'
        interactive
      >
        <Paragraph
          weight={fontWeights.semibold}
          className={classNames('text-gray-dark-charcoal underline-dashed inline-block mb-3 cursor-default')}
          variant='display'
          variantSize='large'
        >
          {translate('standalone.customDomains.trackingCode.formTitle')}
        </Paragraph>
      </TooltipWrapper>

      <CustomWebsiteDomainForm
        inputTooltip={inputTooltip}
        editEnabled={isCustomDomainEditEnabled}
        overrideInputValue={isCustomDomainEditEnabled ? undefined : agencySnippetDomain}
        websiteId={websiteId}
        afterSave={({ customDomainId, domain, resetForm }) => {
          confirm(
            () => {},
            ({ onClose }) => (
              <CustomTrackingCodeDomainCreationModal
                onSuccess={() => {
                  resetForm();
                }}
                domain={domain}
                isOpen={true}
                closeModal={onClose}
                customDomainId={customDomainId}
              />
            ),
          );
        }}
        actions={{
          active: actions.activeCustomDomainActions,
          pending: actions.pendingCustomDomainActions,
          inactive: actions.inactiveCustomDomainsActions,
        }}
        domainType={CustomDomainType.trackingCode}
        platform={CustomDomainPlatformType.STANDALONE}
        onError={(err) => {
          showErrorNotification(undefined, err);
        }}
        onActiveDomainChange={() => {
          dispatch(getTrackingCode());
        }}
      />
    </>
  );
};
